import React, { useMemo, useState } from "react";
import { Card, Table } from "antd";
import type { TableColumnsType } from "antd";

import { useProductCategories } from "hooks/useProductCategories";

interface DetectionSummaryResponse {
	detection_summary?: {
		categories_detected?: Array<{
			category: string;
			quantity: number;
			confidence: string;
		}>;
	};
	analysis_notes?: string;
}

interface HandoverDeviceDetectionSummaryProps {
	/** The AI response containing detection summary data */
	aiResponse: string | object | null | undefined;
	/** The detected installed products (JSON string or parsed array) */
	detectedInstalledProducts: string | any[] | null | undefined;
	/** The current products grouped by type (updated state from Zustand store) */
	productsGroupedByType?: any[];
	/** Loading state for the table */
	isLoading?: boolean;
	allInstalledProductsSkipped?: boolean;
}

/**
 * HandoverDeviceDetectionSummary
 *
 * Renders the detection summary card showing:
 * - Total device types found
 * - Total installed devices found
 * - Categories table with type, quantity, and confidence
 * - AI analysis notes
 *
 */
const HandoverDeviceDetectionSummary: React.FC<HandoverDeviceDetectionSummaryProps> = ({
	aiResponse,
	detectedInstalledProducts,
	productsGroupedByType,
	isLoading = false,
	allInstalledProductsSkipped = false
}) => {
	const { getLabelFromName } = useProductCategories();
	const [isExpanded, setIsExpanded] = useState(true);

	// Table columns configuration for categories table
	const tableColumns: TableColumnsType<any> = useMemo(
		() => [
			{
				title: "TYPE",
				dataIndex: "category",
				render: (_field: any, record: any) => getLabelFromName(record.category),
				key: "TYPE"
			},
			{
				title: "INSTALLED",
				dataIndex: "quantity",
				key: "INSTALLED"
			},
			{
				title: "CONFIDENCE",
				dataIndex: "confidence",
				render: (_field: any, record: any) => (
					<span className={`hdd-confidence ${record.confidence}`}>{record.confidence}</span>
				),
				key: "CONFIDENCE"
			}
		],
		[getLabelFromName]
	);

	// Calculate categories from productsGroupedByType if available
	const categoriesFromGroupedProducts = useMemo(() => {
		if (!productsGroupedByType || productsGroupedByType.length === 0) return null;

		return productsGroupedByType
			.filter(group => group.totalProducts > 0) // Only include categories with products
			.map(group => ({
				category: group.product_type?.name || "UNCATEGORIZED",
				quantity: group.totalInstalledProducts,
				confidence: "high" // Default confidence since these are edited/confirmed
			}));
	}, [productsGroupedByType]);

	// Calculate total installed products from productsGroupedByType
	const totalInstalledFromGrouped = useMemo(() => {
		if (!productsGroupedByType || productsGroupedByType.length === 0) return null;
		return productsGroupedByType.reduce((sum, group) => sum + (group.totalInstalledProducts || 0), 0);
	}, [productsGroupedByType]);

	// ── Render ───────────────────────────────────────────────────────────────

	if (!aiResponse) return null;

	try {
		const response: DetectionSummaryResponse = typeof aiResponse === "string" ? JSON.parse(aiResponse) : aiResponse;

		if (!response.detection_summary) return null;

		// Use categoriesFromGroupedProducts if available (edited state), otherwise use AI response
		const categories_detected = categoriesFromGroupedProducts || response.detection_summary.categories_detected;
		const categoriesCount = categories_detected?.length ?? 0;

		// Use totalInstalledFromGrouped if available, otherwise calculate from detectedInstalledProducts
		let totalInstalled = totalInstalledFromGrouped;
		if (totalInstalled === null) {
			const installations =
				typeof detectedInstalledProducts === "string"
					? JSON.parse(detectedInstalledProducts)
					: detectedInstalledProducts;
			totalInstalled = installations?.length ?? 0;
		}

		return (
			<Card className="hdd-summary-card">
				{/* Header with expand/collapse button */}
				<div className="hdd-section-header">
					<img
						src={
							isExpanded
								? `${process.env.PUBLIC_URL}/assets/images/icons/chevron-up-gray.svg`
								: `${process.env.PUBLIC_URL}/assets/images/icons/chevron-down-gray.svg`
						}
						alt={isExpanded ? "collapse" : "expand"}
						className="hdd-expand-icon"
						onClick={() => setIsExpanded(!isExpanded)}
					/>
					<span className="hdd-section-header-title">Summary</span>
				</div>

				{/* Collapsible content */}
				{isExpanded && (
					<div className="hdd-expandable">
						<div className="basic-row">
							<div className="hdd-stat-item">
								<span className="hdd-summary-total-label">Total device types found:</span>
								<span className="hdd-summary-total-count">{categoriesCount}</span>
							</div>
							<div className="hdd-stat-item right">
								<span className="hdd-summary-total-label">Total Installed Devices Found:</span>
								<span className="hdd-summary-total-count">{totalInstalled}</span>
							</div>
						</div>

						{categories_detected && categories_detected.length > 0 && (
							<div className="hdd-categories-table-container">
								<Table dataSource={categories_detected} columns={tableColumns} loading={isLoading} pagination={false} />
							</div>
						)}

						{response.analysis_notes && (
							<div className="hdd-summary-analysis-row">
								<span className="hdd-summary-label">Analysis:</span>
								<p className="hdd-description">{response.analysis_notes}</p>
							</div>
						)}

						{allInstalledProductsSkipped && (
							<div className="hdd-sum-no-devices-alert">
								<img
									src={`${process.env.PUBLIC_URL}/assets/images/icons/info-circle-2.svg`}
									alt=""
									className="hdd-sum-no-devices-alert-icon"
								/>
								<div className="hdd-sum-no-devices-alert-content">
									<p className="hdd-sum-no-devices-alert-title">Update required: No new devices detected</p>
									<p className="hdd-sum-no-devices-alert-body">
										All devices and equipment identified during automagical detection were already present in the
										property&#39;s records with matching serial numbers. Please update the information below to include
										newly installed devices, then re-submit.
									</p>
								</div>
							</div>
						)}
					</div>
				)}
			</Card>
		);
	} catch (error) {
		console.error("Error parsing detection summary:", error);
		return null;
	}
};

export default HandoverDeviceDetectionSummary;
